import { Grid } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { ConfirmationDialog } from "../../../common/components/functional/confirmationDialog/ConfirmationDialog";
import { Nullable } from "../../../common/types/nullable";
import useIsLoading from "../../../hooks/useIsLoading";
import useMessage from "../../../hooks/useMessage";
import DashboardHeaderRow from "../DashboardHeaderRow";
import { hrTaskAcknowledge } from "../slice/dashboardActions";
import { selectAllHrTaskResponses } from "../slice/dashboardSlice";
import HrTasklist from "./HrTasklist";
import HrTaskContentRows from "./HrTaskContentRows";
import { toHrTask } from "./hrTaskAdapter";

const HrTasklistContainer = () => {
  const [selectedHrTaskId, setSelectedHrTaskId] =
    useState<Nullable<string>>(null);
  const localized = useAppSelector((state) => state.localized);
  const responses = useAppSelector(selectAllHrTaskResponses);
  const { t, i18n } = useTranslation("dashboard");
  const language = i18n.language;
  const tasks = useMemo(
    () =>
      responses.map((response) =>
        toHrTask(response, { language, t, localized }),
      ),
    [responses, language, t, localized],
  );
  const selectedHrTask = tasks.find((task) => task.id === selectedHrTaskId);
  const { sendMessage } = useMessage();
  const dispatch = useAppDispatch();
  const isLoading = useIsLoading(["dashboard.hrTasklist"]);

  useEffect(() => {
    responses.forEach((response) => {
      if ((response.presentation != null) !== (response.actions != null)) {
        console.warn(
          "HR task has incomplete presentation/actions",
          response.id,
        );
      }
    });
  }, [responses]);

  const onDialogClose = () => setSelectedHrTaskId(null);

  const onDialogConfirm = async () => {
    if (!selectedHrTask) return;

    const action = await dispatch(
      hrTaskAcknowledge({
        hrTaskId: selectedHrTask.id,
        stamp: Date.now(),
      }),
    );
    if (isFulfilled(action)) {
      sendMessage(t("hrTasklist.taskMarkedAsDone"), "success");
      onDialogClose();
    }
  };

  return (
    <Grid item xs={3.1}>
      <DashboardHeaderRow title={t("hrTasklist.title")} />
      <HrTasklist
        isLoading={isLoading}
        tasks={tasks}
        selectedHrTaskId={selectedHrTaskId}
        handleSelectHrTask={setSelectedHrTaskId}
      />
      <ConfirmationDialog
        isOpen={!!selectedHrTask}
        title={selectedHrTask?.actions.confirmation.title ?? ""}
        contentText={
          selectedHrTask ? (
            <HrTaskContentRows
              rows={selectedHrTask.actions.confirmation.rows}
            />
          ) : (
            ""
          )
        }
        confirmText={t("hrTasklist.confirmButtonText")}
        cancelText={t("common:cancel")}
        onConfirm={onDialogConfirm}
        onCancel={onDialogClose}
        isLoading={isLoading}
      />
    </Grid>
  );
};

export default HrTasklistContainer;
